import { useEffect, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import useSEO from "@/hooks/useSEO";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ComicCard from "@/components/base/ComicCard";
import { getComicById, getRecommendedComics } from "@/mocks/comics";

const siteUrl = "https://hanhanmanhua.com";

export default function ComicDetailPage() {
  const { comicId } = useParams<{ comicId: string }>();
  const id = parseInt(comicId || "1", 10);
  const comic = getComicById(id);
  const recommended = useMemo(
    () => (comic ? getRecommendedComics(id, 4) : []),
    [comic, id]
  );

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [comicId]);

  useSEO({
    title: comic ? `${comic.title} - 汗汗漫画` : "漫画详情 - 汗汗漫画",
    description: comic
      ? `${comic.title}，${comic.description} 作者：${comic.author}。汗汗漫画免费在线阅读，极速更新，无遮挡阅读体验。`
      : "汗汗漫画提供海量免费漫画在线阅读，极速更新，无遮挡阅读体验。",
    keywords: comic
      ? `${comic.title},${comic.author},${comic.tags.join(",")},免费漫画`
      : "汗汗漫画,免费漫画,在线漫画",
    canonicalUrl: `${siteUrl}/comic/${id}`,
    ogImage: comic?.cover,
    ogType: "article",
    jsonLd: comic
      ? [
          {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: `${comic.title} - 汗汗漫画`,
            description: comic.description,
            author: {
              "@type": "Person",
              name: comic.author,
            },
            datePublished:
              comic.chapters[comic.chapters.length - 1]?.date || "2026-01-01",
            dateModified: comic.chapters[0]?.date || "2026-08-04",
            image: comic.cover,
            publisher: {
              "@type": "Organization",
              name: "汗汗漫画",
            },
            mainEntityOfPage: {
              "@type": "WebPage",
              "@id": `${siteUrl}/comic/${comic.id}`,
            },
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: siteUrl },
              {
                "@type": "ListItem",
                position: 2,
                name: comic.tags[0] || "全部",
                item: `${siteUrl}/category/${comic.category}`,
              },
              {
                "@type": "ListItem",
                position: 3,
                name: comic.title,
              },
            ],
          },
        ]
      : undefined,
  });

  if (!comic) {
    return (
      <main className="pt-20">
        <div className="w-full px-4 md:px-8 py-20 text-center">
          <h1 className="text-xl font-bold text-foreground-950">漫画未找到</h1>
          <Link
            to="/"
            className="mt-4 inline-block text-primary-500 hover:text-primary-600 cursor-pointer"
          >
            返回首页
          </Link>
        </div>
      </main>
    );
  }

  const formatViews = (v: number) => {
    if (v >= 10000) return `${(v / 10000).toFixed(1)}万`;
    return v.toString();
  };

  return (
    <main>
      <div className="pt-20 bg-background-50">
        <Breadcrumb
          items={[
            { label: "首页", to: "/" },
            {
              label: comic.tags[0] || "全部",
              to: `/category/${comic.category}`,
            },
            { label: comic.title },
          ]}
        />

        {/* Comic Header */}
        <div className="w-full px-4 md:px-8 py-6">
          <div className="max-w-[1400px] mx-auto">
            <div className="flex flex-col md:flex-row gap-6 md:gap-8">
              <div className="flex-shrink-0 w-40 md:w-52 mx-auto md:mx-0">
                <div className="aspect-[3/4] rounded-lg overflow-hidden bg-background-100">
                  <img
                    src={comic.cover}
                    alt={comic.title}
                    title={`${comic.title} - 汗汗漫画免费在线阅读`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <h1 className="text-xl md:text-2xl font-bold text-foreground-950 mb-2">
                  {comic.title}
                </h1>
                <div className="flex flex-wrap items-center gap-3 text-sm text-foreground-500 mb-3">
                  <span>作者：{comic.author}</span>
                  <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                  <span>{formatViews(comic.views)} 阅读</span>
                  <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                  <span className="flex items-center gap-1">
                    <i className="ri-star-fill text-amber-500 text-xs"></i>
                    {comic.rating}
                  </span>
                </div>

                <div className="flex flex-wrap gap-2 mb-4">
                  {comic.tags.map((tag) => (
                    <Link
                      key={tag}
                      to={`/category/${comic.category}`}
                      className="text-xs px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 hover:bg-secondary-200 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      {tag}
                    </Link>
                  ))}
                  <span
                    className={`text-xs px-3 py-1 rounded-full font-medium whitespace-nowrap ${
                      comic.status === "连载中"
                        ? "bg-accent-100 text-accent-700"
                        : "bg-background-200 text-foreground-500"
                    }`}
                  >
                    {comic.status}
                  </span>
                  <span className="text-xs px-3 py-1 rounded-full bg-background-100 text-foreground-500 whitespace-nowrap">
                    更新：{comic.updateTime}
                  </span>
                </div>

                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  {comic.description}
                </p>

                <div className="flex flex-wrap gap-3">
                  <button className="px-6 py-2.5 rounded-full bg-primary-500 text-white font-semibold text-sm hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap">
                    开始阅读
                  </button>
                  <button className="px-6 py-2.5 rounded-full bg-background-100 text-foreground-700 font-medium text-sm hover:bg-background-200 transition-all cursor-pointer whitespace-nowrap border border-background-200/50">
                    <i className="ri-bookmark-line mr-1"></i> 收藏
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Chapters */}
        <div className="w-full px-4 md:px-8 py-8 bg-background-100">
          <div className="max-w-[1400px] mx-auto">
            <h2 className="text-lg font-bold text-foreground-950 mb-4">
              章节列表
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {comic.chapters.map((chapter) => (
                <div
                  key={chapter.num}
                  className="flex items-center justify-between p-3 rounded-lg bg-background-50 hover:bg-background-200/70 transition-colors cursor-pointer border border-background-200/30"
                >
                  <div className="flex items-center gap-3">
                    <span className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 text-xs font-bold">
                      {chapter.num}
                    </span>
                    <span className="text-sm font-medium text-foreground-800">
                      第{chapter.num}话 {chapter.title}
                    </span>
                  </div>
                  <span className="text-xs text-foreground-400 whitespace-nowrap">
                    {chapter.date}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Recommended */}
        {recommended.length > 0 && (
          <div className="w-full px-4 md:px-8 py-10 bg-background-50">
            <div className="max-w-[1400px] mx-auto">
              <h2 className="text-lg font-bold text-foreground-950 mb-5">
                猜你喜欢
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5">
                {recommended.map((item) => (
                  <ComicCard key={item.id} {...item} />
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </main>
  );
}